import React from 'react';
import { get } from 'lodash';
import moment from 'moment';
import { useQuery } from 'react-query';
import { buildDimensionQuery } from './utils';
import { getDimensionData } from '@api/dimensionContainer';
import instance from '@api/axiosInstance';
import getQueryTag from '@utils/misc/queryTag';
import { checkIfSampledCluster } from '@components/common/SampledDataWarning';
import { isAppModule } from '@src/utils/constants';
import { addLocalPercentageMetrics } from '@src/utils/table/parseUtils';


export function flattenData(res, query) {
  const { data: _data, totals: _totals } = get(res, ['0'], {});
  const totals = get(_totals, ['0', 'results']);
  const rows = get(_data, ['0', 'results']);

  const adjustedInterval = get(
    res,
    '[0].annotations[0].value',
    `${moment().format()}/${moment().format()}`,
  );
  addLocalPercentageMetrics(query.metrics, rows, totals);
  return { totals, rows, adjustedInterval };
}

function useFetchGroupBy({
  dimension,
  metrics,
  filter,
  interval,
  metricFilter = null,
  enabled = true,
  sortByMetric,
  limit = 200,
  realTimeOnTrends,
  noBrushSelected = false,
  comparisonInterval = null,
  isTableCompareEnabled = false,
  dataScope,
  usecaseKey,
}: any) {
  const { _rKey = 0, rule } = filter;

  const query: any = buildDimensionQuery({
    dimension,
    metrics,
    filter: rule,
    interval: `${moment(interval.from).format()}/${moment(interval.to).format()}`,
    sortByMetric,
  });

  const comparisonIntervalString = comparisonInterval && `${moment(comparisonInterval.from).format()}/${moment(comparisonInterval.to).format()}`;
  query.limit = limit;
  query.isLiveMode = realTimeOnTrends;

  if (realTimeOnTrends && noBrushSelected) {
    query.ignoreInterval = true;
  }

  if (isTableCompareEnabled || (metricFilter && metricFilter.length > 0)) {
    query.metricFilter = metricFilter;
    query.type = 'multi-group-by';
    query.comparisonInterval = comparisonIntervalString;
  }

  if (isAppModule) {
    query.options.dataScope = dataScope;
    query.options.presetKey = usecaseKey;
  }
  const queryTag = getQueryTag({ liveMode: realTimeOnTrends });
  return useQuery(
    ['dimension-groupby', query, _rKey],
    () => {
      return getDimensionData({ queries: [query] }, { headers: { ...queryTag } }, instance).then(
        (response) => {
          checkIfSampledCluster(get(response, ['data', 0, 'annotations'], []));
          if (query.type === 'multi-group-by') {
            const { metadataFilterData, metricFilterData } = get(response, ['data'], {});
            return {
              metadataFilterData: flattenData(metadataFilterData, query),
              metricFilterData: flattenData(metricFilterData, query),
            };
          } else {
            return flattenData(response.data, query);
          }
        },
      );
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: enabled && metrics.length > 0 && !!sortByMetric,
      keepPreviousData: realTimeOnTrends, // loading is disable on RT mode, enable to avoid showing no data layer
    },
  );
}

export default useFetchGroupBy;
